import { useFormContext } from "react-hook-form";
import { FormControlLabel, Stack, Switch, IconButton, Popover, Typography } from "@mui/material";
import { useTranslatedMessages } from "@/multiLanguage/i18n";
import HelpIcon from "@mui/icons-material/Help";
import { useEffect, useState } from "react";
import { theme } from "@/theme";

interface InputData {
    arteriopathy?: boolean;
    /**
     * Mostra la domanda sulle placche presenti in più di un distretto
     * arterioso, che da sola porta al rischio estremo. Prevista solo dalla
     * scala open.
     */
    multiDistrictStenosis?: boolean;
}
function CardiovascularEventsData({
    arteriopathy = true,
    multiDistrictStenosis = false,
}: InputData) {
    const lang = useTranslatedMessages();
    const { watch, setValue, register } = useFormContext();
    const [infoTextIsOpen, setInfoTextIsOpen] = useState<boolean>(false);
    const [infoText, setInfoText] = useState<string>("");
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const isCardiovascularEvent = watch("cardiovascularEventSwitch");
    const isArteriopathy = watch("arteriopathy");

    // Se non c'è stato nessun evento, non possono esserci due o più eventi.
    // Va azzerato quando il flag padre è spento, non quando viene acceso:
    // altrimenti si perde il valore impostato dall'esterno (es. il
    // precompilamento delle casistiche dello strumento dev).
    useEffect(() => {
        if (!isCardiovascularEvent) {
            setValue("moreCardiovascularEvent", false);
        }
    }, [isCardiovascularEvent, setValue]);

    useEffect(() => {
        if (!isArteriopathy) {
            setValue("arteryStenosis", false);
        }
    }, [isArteriopathy, setValue]);

    // Se la domanda è visibile il campo deve partire da "no", altrimenti finché
    // l'utente non la tocca resta undefined e sparisce dai dati inviati.
    const hasMultiDistrictStenosis = watch("multiDistrictStenosis");
    useEffect(() => {
        if (multiDistrictStenosis && hasMultiDistrictStenosis === undefined) {
            setValue("multiDistrictStenosis", false);
        }
    }, [multiDistrictStenosis, hasMultiDistrictStenosis, setValue]);

    return (
        <Stack gap={0}>
            {/* <Typography variant='h4'>{lang.scales.cardiovascularEventsComponent.title} </Typography>
             */}

            <FormControlLabel
                control={
                    <Stack flexDirection={"row"} alignItems={"center"}>
                        <Switch
                            {...register("cardiovascularEventSwitch")}
                            checked={!!isCardiovascularEvent}
                            data-testid="form-component-cardiovascularEventSwitch"
                            onChange={(e) =>
                                setValue("cardiovascularEventSwitch", e.target.checked)
                            }
                        />
                        <IconButton
                            onClick={(event) => {
                                setAnchorEl(event.currentTarget);
                                setInfoText(
                                    lang.scales.cardiovascularEventsComponent.cardiovascularEvent
                                        .infoText
                                );
                                setInfoTextIsOpen(true);
                            }}
                            edge="end"
                        >
                            <HelpIcon sx={{ color: theme.palette.secondary.light }} />
                        </IconButton>
                    </Stack>
                }
                label={lang.scales.cardiovascularEventsComponent.cardiovascularEvent.label}
                labelPlacement="start"
                sx={{
                    marginLeft: 0,
                    marginTop: "10px",
                    marginBottom: "10px",
                    flexGrow: 1,
                    "&.MuiFormControlLabel-labelPlacementStart": {
                        justifyContent: "space-between",
                    },
                }}
            />

            {isCardiovascularEvent && (
                <FormControlLabel
                    control={
                        <Switch
                            {...register("moreCardiovascularEvent")}
                            checked={!!watch("moreCardiovascularEvent")}
                            data-testid="form-component-moreCardiovascularEvent"
                            onChange={(e) => setValue("moreCardiovascularEvent", e.target.checked)}
                        />
                    }
                    label={
                        lang.scales.cardiovascularEventsComponent.cardiovascularEvent
                            .moreCardiovascularEvent
                    }
                    labelPlacement="start"
                    sx={{
                        marginLeft: 0,
                        marginTop: "10px",
                        marginBottom: "10px",
                        flexGrow: 1,
                        "&.MuiFormControlLabel-labelPlacementStart": {
                            justifyContent: "space-between",
                        },
                    }}
                />
            )}

            {multiDistrictStenosis && (
                <FormControlLabel
                    control={
                        <Switch
                            {...register("multiDistrictStenosis")}
                            checked={!!hasMultiDistrictStenosis}
                            data-testid="form-component-multiDistrictStenosis"
                            onChange={(e) => setValue("multiDistrictStenosis", e.target.checked)}
                        />
                    }
                    label={lang.scales.cardiovascularEventsComponent.multiDistrictStenosis.label}
                    labelPlacement="start"
                    sx={{
                        marginLeft: 0,
                        marginTop: "10px",
                        marginBottom: "10px",
                        flexGrow: 1,
                        "&.MuiFormControlLabel-labelPlacementStart": {
                            justifyContent: "space-between",
                        },
                    }}
                />
            )}

            {arteriopathy ? (
                <FormControlLabel
                    control={
                        <Stack flexDirection={"row"} alignItems={"center"}>
                            <Switch
                                {...register("arteriopathy")}
                                checked={!!isArteriopathy}
                                data-testid="form-component-arteriopathy"
                                onChange={(e) => setValue("arteriopathy", e.target.checked)}
                            />
                            <IconButton
                                onClick={(event) => {
                                    setAnchorEl(event.currentTarget);
                                    setInfoText(
                                        lang.scales.cardiovascularEventsComponent.arteriopathy
                                            .infoText
                                    );
                                    setInfoTextIsOpen(true);
                                }}
                                edge="end"
                            >
                                <HelpIcon sx={{ color: theme.palette.secondary.light }} />
                            </IconButton>
                        </Stack>
                    }
                    label={lang.scales.cardiovascularEventsComponent.arteriopathy.label}
                    labelPlacement="start"
                    sx={{
                        marginLeft: 0,
                        marginTop: "10px",
                        marginBottom: "10px",
                        flexGrow: 1,
                        "&.MuiFormControlLabel-labelPlacementStart": {
                            justifyContent: "space-between",
                        },
                    }}

                />
            ) : null}

            {arteriopathy && isArteriopathy ? (
                <FormControlLabel
                    control={
                        <Stack flexDirection={"row"} alignItems={"center"}>
                            <Switch
                                {...register("arteryStenosis")}
                                checked={!!watch("arteryStenosis")}
                                data-testid="form-component-arteryStenosis"
                                onChange={(e) => setValue("arteryStenosis", e.target.checked)}
                            />
                            <IconButton
                                onClick={(event) => {
                                    setAnchorEl(event.currentTarget);
                                    setInfoText(
                                        lang.scales.cardiovascularEventsComponent.arteryStenosis
                                            .infoText
                                    );
                                    setInfoTextIsOpen(true);
                                }}
                                edge="end"
                            >
                                <HelpIcon sx={{ color: theme.palette.secondary.light }} />
                            </IconButton>
                        </Stack>
                    }
                    label={lang.scales.cardiovascularEventsComponent.arteryStenosis.label}
                    labelPlacement="start"
                    sx={{
                        marginLeft: 0,
                        marginTop: "10px",
                        marginBottom: "10px",
                        flexGrow: 1,
                        "&.MuiFormControlLabel-labelPlacementStart": {
                            justifyContent: "space-between",
                        },
                    }}
                />
            ) : null}
            <Popover
                open={infoTextIsOpen}
                anchorEl={anchorEl}
                onClose={() => {
                    setInfoTextIsOpen(false);
                }}
                disableScrollLock
                transformOrigin={{
                    horizontal: "left",
                    vertical: "bottom",
                }}
            >
                <Typography sx={{ p: 2 }}>{infoText}</Typography>
            </Popover>
        </Stack>
    );
}

export default CardiovascularEventsData;
